import type { GarageProperty } from "@App/apis/graphql/perimeterSearch/responses/garageProperty";
import type { Garage, Organization, PremiumService, Service } from "@App/types";

export const TALLY_CALLBACK_BASE_URL = "https://tl.repareo.de/r/xX4RAJ";

export const TALLY_FIELD_KEYS = {
  organizationId: "organization_id",
  organizationName: "organization_name",
  garageId: "garage_id",
  garageChainId: "garage_chain_id",
  garageName: "garage_name",
  garageAddress: "garage_address",
  serviceId: "service_id",
  service: "service",
  additionalServiceIds: "additional_service_ids",
  additionalServiceNames: "additional_service_names",
  garageProperties: "garage_properties",
} as const;

interface BuildCallbackRequestUrlParams {
  organization: Organization | null;
  garage: Garage | null;
  service: Service | null;
  premiumServices: PremiumService[] | null;
  garageProperties: GarageProperty[] | null;
}

const formatAddress = (garage: Garage | null): string => {
  if (!garage) return "";
  const street = garage.street?.trim();
  const cityLine = [garage.postcode?.trim(), garage.city?.trim()].filter(Boolean).join(" ");
  return [street, cityLine].filter(Boolean).join(", ");
};

const joinNames = <T extends { name: string }>(items: T[] | null): string =>
  (items ?? [])
    .map((item) => item.name)
    .filter(Boolean)
    .join(", ");

const joinIds = <T extends { id: number | string }>(items: T[] | null): string =>
  (items ?? []).map((item) => item.id).join(", ");

export const buildCallbackRequestUrl = ({
  organization,
  garage,
  service,
  premiumServices,
  garageProperties,
}: BuildCallbackRequestUrlParams): string => {
  const params = new URLSearchParams();

  const set = (key: string, value?: string | number | null) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  };

  set(TALLY_FIELD_KEYS.organizationId, organization?.id);
  set(TALLY_FIELD_KEYS.organizationName, organization?.name);
  set(TALLY_FIELD_KEYS.garageId, garage?.id);
  set(TALLY_FIELD_KEYS.garageChainId, garage?.garageChain?.id);
  set(TALLY_FIELD_KEYS.garageName, garage?.name);
  set(TALLY_FIELD_KEYS.garageAddress, formatAddress(garage));
  set(TALLY_FIELD_KEYS.serviceId, service?.id);
  set(TALLY_FIELD_KEYS.service, service?.pivot?.display_name || service?.name);
  set(TALLY_FIELD_KEYS.additionalServiceIds, joinIds(premiumServices));
  set(TALLY_FIELD_KEYS.additionalServiceNames, joinNames(premiumServices));
  set(TALLY_FIELD_KEYS.garageProperties, joinNames(garageProperties));

  const query = params.toString();
  return query ? `${TALLY_CALLBACK_BASE_URL}?${query}` : TALLY_CALLBACK_BASE_URL;
};
